import { useEffect, useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Combobox, type IComboboxOption } from '../../shared/ui/combobox';
import { FieldLabel } from '../../shared/ui/field-label';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import {
  FOLDER_MAX_DEPTH,
  SCOPE_ROOT_LABEL,
  countLine,
  folderKeys,
  listFolders,
  pathText,
  subtreeHeight,
  subtreeIds,
  type TFolder,
  type TFolderScope,
} from '../../entities/folder';

// A value no folder id can take (`fld_…`): the combobox needs a string for "the top level".
const ROOT = '__root';

interface IMoveToFolderProps {
  scope: TFolderScope;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Move 3 flows", "Move “Billing”". */
  title: string;
  /** Where the things are now: offered, but moving there changes nothing. */
  from?: string | null;
  /** A folder being moved cannot go into itself, below itself, or deeper than the limit. */
  folder?: TFolder;
  noun: string;
  onMove: (folderId: string | null) => Promise<unknown>;
}

export const MoveToFolder = ({
  scope,
  open,
  onOpenChange,
  title,
  from,
  folder,
  noun,
  onMove,
}: IMoveToFolderProps) => {
  const folders = useQuery({
    queryKey: folderKeys.list(scope),
    queryFn: () => listFolders(scope),
    enabled: open,
  });
  const [target, setTarget] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [waiting, setWaiting] = useState(false);
  const fieldId = useId();

  useEffect(() => {
    if (!open) return;
    setTarget('');
    setError(null);
  }, [open]);

  const options = useMemo((): IComboboxOption[] => {
    const all = folders.data?.data ?? [];
    const inside = folder ? subtreeIds(all, folder.id) : new Set<string>();
    const height = folder ? subtreeHeight(all, folder.id) : 1;
    const why = (candidate: TFolder | null): string | null => {
      const id = candidate?.id ?? null;
      if (from !== undefined && id === from) return 'It is here now';
      if (candidate && inside.has(candidate.id)) return 'Inside the folder being moved';
      if (folder && (candidate?.depth ?? 0) + height > FOLDER_MAX_DEPTH) {
        return `Would go deeper than ${FOLDER_MAX_DEPTH} levels`;
      }
      return null;
    };
    const rootWhy = why(null);
    return [
      {
        value: ROOT,
        label: `${SCOPE_ROOT_LABEL[scope]} (top level)`,
        description: rootWhy ?? undefined,
        disabled: rootWhy !== null,
      },
      ...all.map((candidate) => {
        const reason = why(candidate);
        return {
          value: candidate.id,
          label: pathText(candidate.path),
          description: reason ?? countLine(candidate, noun),
          disabled: reason !== null,
        };
      }),
    ];
  }, [folders.data, folder, from, scope, noun]);

  const submit = async () => {
    if (!target) return;
    setWaiting(true);
    setError(null);
    try {
      await onMove(target === ROOT ? null : target);
      onOpenChange(false);
    } catch (caught) {
      setError(caught);
    } finally {
      setWaiting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title={title}
      submit={{
        label: 'Move',
        onSubmit: () => void submit(),
        disabled: !target,
        waiting,
        waitingLabel: 'Moving',
      }}
    >
      <div className="flex flex-col gap-4 pt-2">
        <div>
          <FieldLabel htmlFor={fieldId}>To</FieldLabel>
          <Combobox
            id={fieldId}
            aria-label="Folder to move to"
            noun="folder"
            placeholder="Pick a folder"
            value={target}
            onChange={setTarget}
            options={options}
            disabled={folders.isPending}
          />
        </div>
        <MutationError error={error ?? folders.error} />
      </div>
    </Modal>
  );
};
